import type { Shift, ShiftStatus, StaffMember, Weekday, WeeklyRoster } from '../types';
import { ensureUniqueShiftIds, newRosterShiftId } from './rosterMerge';

/** Machine-readable roster backup. Excel/PDF remain for print — use JSON for accurate round-trip. */
export const ROSTER_JSON_FORMAT = 'kingspark-roster-v1' as const;

const WEEK_DAYS: Weekday[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const SHIFT_STATUSES: ShiftStatus[] = ['open', 'scheduled', 'confirmed'];

export type RosterJsonStaffHint = {
  staffId: number | null;
  staffFirestoreId?: string;
  name?: string;
};

export type RosterJsonDocument = {
  format: typeof ROSTER_JSON_FORMAT;
  exportedAt: number;
  weekLabel?: string;
  /** Roster as stored (shifts include full fields). */
  roster: WeeklyRoster;
  /** Optional name hints for humans / soft staff remapping on import. */
  staffHints?: RosterJsonStaffHint[];
};

export type ApplyRosterJsonOptions = {
  /** Week currently open in the UI — shifts are remapped onto this Monday. */
  targetWeekStartMs: number;
  timezone: string;
  staffMembers?: StaffMember[];
  /**
   * When true (default), clear timesheet actualHours — planned copy into another week.
   * When importing a backup of the *same* week you may pass false.
   */
  clearActualHours?: boolean;
};

export type ApplyRosterJsonResult =
  | { ok: true; roster: WeeklyRoster; warnings: string[]; shiftCount: number }
  | { ok: false; error: string };

function isWeekday(v: unknown): v is Weekday {
  return typeof v === 'string' && (WEEK_DAYS as string[]).includes(v);
}

function dateForDay(weekStartMs: number, day: Weekday): number {
  const idx = WEEK_DAYS.indexOf(day);
  const d = new Date(weekStartMs);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + idx);
  return d.getTime();
}

function normalizeHhmm(raw: unknown, fallback: string): string {
  if (typeof raw !== 'string') return fallback;
  const m = raw.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return fallback;
  const h = Math.max(0, Math.min(23, parseInt(m[1], 10)));
  const min = Math.max(0, Math.min(59, parseInt(m[2], 10)));
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** Build a downloadable JSON document from the live weekly roster. */
export function buildRosterJsonDocument(
  roster: WeeklyRoster,
  opts?: { weekLabel?: string; staffMembers?: StaffMember[] }
): RosterJsonDocument {
  const staffHints: RosterJsonStaffHint[] = [];
  const seen = new Set<string>();
  for (const s of roster.shifts) {
    const key = s.staffFirestoreId ?? (s.staffId != null ? `id:${s.staffId}` : '');
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const member =
      opts?.staffMembers?.find((m) => {
        const fs = (m as { firestoreId?: string }).firestoreId;
        if (s.staffFirestoreId && fs && fs === s.staffFirestoreId) return true;
        return s.staffId != null && m.id === s.staffId;
      }) ?? null;
    staffHints.push({
      staffId: s.staffId,
      staffFirestoreId: s.staffFirestoreId,
      name: member?.name,
    });
  }

  return {
    format: ROSTER_JSON_FORMAT,
    exportedAt: Date.now(),
    weekLabel: opts?.weekLabel,
    roster: {
      id: roster.id,
      weekStart: roster.weekStart,
      timezone: roster.timezone,
      shifts: roster.shifts.map((s) => ({ ...s })),
      notes: roster.notes,
      status: roster.status ?? 'draft',
      createdAt: roster.createdAt,
      updatedAt: roster.updatedAt,
      submittedAt: roster.submittedAt,
      submittedBy: roster.submittedBy,
      approvedAt: roster.approvedAt,
      approvedBy: roster.approvedBy,
    },
    staffHints: staffHints.length ? staffHints : undefined,
  };
}

export function downloadRosterJson(doc: RosterJsonDocument, filename?: string): void {
  const week = doc.roster.weekStart;
  const name =
    filename ??
    `roster-week-${new Date(week).toISOString().slice(0, 10)}.json`;
  const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function resolveStaff(
  shift: Pick<Shift, 'staffId' | 'staffFirestoreId'>,
  staffMembers: StaffMember[] | undefined,
  nameHint: string | undefined,
  warnings: string[]
): { staffId: number | null; staffFirestoreId?: string } {
  if (!staffMembers?.length) {
    return { staffId: shift.staffId ?? null, staffFirestoreId: shift.staffFirestoreId };
  }

  if (shift.staffFirestoreId) {
    const byFs = staffMembers.find(
      (m) => (m as { firestoreId?: string }).firestoreId === shift.staffFirestoreId
    );
    if (byFs) {
      return {
        staffId: byFs.id,
        staffFirestoreId: (byFs as { firestoreId?: string }).firestoreId,
      };
    }
    warnings.push(`No staff with firestoreId ${shift.staffFirestoreId}; kept numeric id ${shift.staffId ?? '—'}.`);
  }

  if (shift.staffId != null) {
    const byId = staffMembers.find((m) => m.id === shift.staffId);
    if (byId) {
      return {
        staffId: byId.id,
        staffFirestoreId: (byId as { firestoreId?: string }).firestoreId,
      };
    }
  }

  if (nameHint) {
    const byName = staffMembers.find((m) => m.name.trim().toLowerCase() === nameHint.trim().toLowerCase());
    if (byName) {
      warnings.push(`Remapped "${nameHint}" by name (id/firestore id missing).`);
      return {
        staffId: byName.id,
        staffFirestoreId: (byName as { firestoreId?: string }).firestoreId,
      };
    }
    warnings.push(`Could not resolve staff "${nameHint}" — shift left unassigned.`);
    return { staffId: null };
  }

  if (shift.staffId != null) {
    warnings.push(`Staff id ${shift.staffId} not in current staff list — shift left unassigned.`);
  }
  return { staffId: null };
}

function sanitizeShift(
  raw: unknown,
  targetWeekStartMs: number,
  clearActualHours: boolean,
  staffMembers: StaffMember[] | undefined,
  nameByKey: Map<string, string>,
  warnings: string[]
): Shift | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Record<string, unknown>;
  if (!isWeekday(s.day)) {
    warnings.push('Skipped a shift with invalid day.');
    return null;
  }
  const day = s.day;
  const start = normalizeHhmm(s.start, '09:00');
  const end = normalizeHhmm(s.end, '17:00');
  const status: ShiftStatus =
    typeof s.status === 'string' && (SHIFT_STATUSES as string[]).includes(s.status)
      ? (s.status as ShiftStatus)
      : 'scheduled';

  const staffId = typeof s.staffId === 'number' ? s.staffId : s.staffId === null ? null : null;
  const staffFirestoreId = typeof s.staffFirestoreId === 'string' ? s.staffFirestoreId : undefined;
  const hintKey = staffFirestoreId ?? (staffId != null ? `id:${staffId}` : '');
  const resolved = resolveStaff(
    { staffId, staffFirestoreId },
    staffMembers,
    hintKey ? nameByKey.get(hintKey) : undefined,
    warnings
  );

  const mealRaw = typeof s.mealBreakStart === 'string' ? s.mealBreakStart.trim() : '';
  const mealBreakStart = mealRaw ? normalizeHhmm(mealRaw, mealRaw) : undefined;
  if (mealBreakStart && !/^\d{2}:\d{2}$/.test(mealBreakStart)) {
    // invalid meal time — omit
  }
  const unpaidMealMinutes =
    typeof s.unpaidMealMinutes === 'number' && Number.isFinite(s.unpaidMealMinutes)
      ? Math.round(s.unpaidMealMinutes)
      : undefined;

  const shift: Shift = {
    id: newRosterShiftId({ day, staffId: resolved.staffId }),
    day,
    date: dateForDay(targetWeekStartMs, day),
    start,
    end,
    staffId: resolved.staffId,
    staffFirestoreId: resolved.staffFirestoreId,
    status: resolved.staffId == null && status === 'scheduled' ? 'open' : status,
    roleId: typeof s.roleId === 'string' ? s.roleId : undefined,
    notes: typeof s.notes === 'string' ? s.notes : undefined,
    area: typeof s.area === 'string' ? s.area : undefined,
    ...(mealBreakStart && /^\d{2}:\d{2}$/.test(mealBreakStart) ? { mealBreakStart } : {}),
    ...(unpaidMealMinutes != null ? { unpaidMealMinutes } : {}),
  };

  if (!clearActualHours && typeof s.actualHours === 'number' && Number.isFinite(s.actualHours)) {
    shift.actualHours = s.actualHours;
  }

  return shift;
}

/**
 * Parse exported JSON and apply onto `targetWeekStartMs` (the week open in the UI).
 * Always returns status `draft` — re-submit/approve after review. Does not write Firestore.
 */
export function applyRosterJsonToWeek(
  raw: unknown,
  options: ApplyRosterJsonOptions
): ApplyRosterJsonResult {
  const warnings: string[] = [];
  let doc: RosterJsonDocument;

  if (!raw || typeof raw !== 'object') {
    return { ok: false, error: 'File is not a JSON object.' };
  }

  const root = raw as Record<string, unknown>;

  // Accept either full document or a bare WeeklyRoster (with shifts).
  if (root.format === ROSTER_JSON_FORMAT && root.roster && typeof root.roster === 'object') {
    doc = raw as RosterJsonDocument;
  } else if (Array.isArray(root.shifts) && typeof root.weekStart === 'number') {
    doc = {
      format: ROSTER_JSON_FORMAT,
      exportedAt: Date.now(),
      roster: root as unknown as WeeklyRoster,
    };
  } else {
    return {
      ok: false,
      error: `Unrecognized roster file. Expected format "${ROSTER_JSON_FORMAT}".`,
    };
  }

  const source = doc.roster;
  if (!Array.isArray(source.shifts)) {
    return { ok: false, error: 'Roster file has no shifts array.' };
  }

  const clearActual =
    options.clearActualHours !== undefined
      ? options.clearActualHours
      : source.weekStart !== options.targetWeekStartMs;

  const nameByKey = new Map<string, string>();
  for (const h of doc.staffHints ?? []) {
    if (!h.name) continue;
    if (h.staffFirestoreId) nameByKey.set(h.staffFirestoreId, h.name);
    if (h.staffId != null) nameByKey.set(`id:${h.staffId}`, h.name);
  }

  const built: Shift[] = [];
  for (const rawShift of source.shifts) {
    const next = sanitizeShift(
      rawShift,
      options.targetWeekStartMs,
      clearActual,
      options.staffMembers,
      nameByKey,
      warnings
    );
    if (next) built.push(next);
  }

  const { shifts } = ensureUniqueShiftIds(built);
  const now = Date.now();
  const roster: WeeklyRoster = {
    id: `week-${options.targetWeekStartMs}`,
    weekStart: options.targetWeekStartMs,
    timezone: options.timezone || source.timezone || 'Pacific/Auckland',
    shifts,
    notes: typeof source.notes === 'string' ? source.notes : undefined,
    status: 'draft',
    createdAt: now,
    updatedAt: now,
  };

  if (source.weekStart !== options.targetWeekStartMs) {
    warnings.push(
      `Source week ${new Date(source.weekStart).toISOString().slice(0, 10)} applied onto open week ${new Date(options.targetWeekStartMs).toISOString().slice(0, 10)}.`
    );
  }

  return { ok: true, roster, warnings, shiftCount: shifts.length };
}

export async function readRosterJsonFile(file: File): Promise<unknown> {
  const text = await file.text();
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new Error('Could not parse JSON file.');
  }
}
